/**
 * Review A3: once the user chose "Do the same for the other conflicts" on a job, its next prompts are answered with
 * that choice where they offer it — exactly once per prompt — and no dialog opens for them. A prompt that does not
 * offer the choice (an in-job duplicate, an NFS share's missing Overwrite) is asked as usual.
 */
import { useEffect, useRef } from 'react';
import type { TransferJob } from '../../../types/azureStorageTransfers';
import { autoCollisionAction, type CollisionAction } from '../model/collisionRules';
import type { TransferCollisionRule } from '../store/transfersStore';
import type { CollisionDecisionMeta } from './useTransferActions';

type Decide = (jobId: string, item: string, decision: CollisionAction, destination?: string, meta?: CollisionDecisionMeta) => Promise<boolean>;

/** The job whose prompt the dialog should show: `undefined` while the job's rule is answering it. */
export function useCollisionAutoDecide(job: TransferJob | undefined, rule: TransferCollisionRule | undefined, decide: Decide): TransferJob | undefined {
  const pending = job?.pendingCollision;
  // The prompt the rule was set on is already answered; main shows it until it publishes the next one.
  const action = pending && rule && rule.setOn !== pending.item ? autoCollisionAction(pending, rule.action) : undefined;
  const key = job && pending ? `${job.id}\u0000${pending.item}` : undefined;
  const answered = useRef<string>();
  useEffect(() => {
    if (!job || !pending || !action || !key || answered.current === key) return;
    answered.current = key;
    void decide(job.id, pending.item, action, undefined, { auto: true });
  }, [key, action]); // eslint-disable-line react-hooks/exhaustive-deps -- one decision per prompt key
  return action ? undefined : job;
}
